import React, { useState, useEffect, useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { TransientState } from '../../../storage';
import {
    Box,
    Checkbox,
    Chip,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    AdminMlStatus as AdminMlStatusApi,
    AdminMlScaleUp as AdminMlScaleUpApi,
    AdminMlScaleDown as AdminMlScaleDownApi,
    AdminMlAwsLaunchTemplates as AdminMlAwsLaunchTemplatesApi,
} from '../../../api/admin-ml';
import { AdminMenuBar, LaunchInstanceDialog } from '../..';
import { adminDeepLink } from '../../utils/utils';

const HOURLY_PRICE = {
    't4g.nano': 0.0042,
    't4g.micro': 0.0084,
    't4g.small': 0.0168,
    't4g.medium': 0.0336,
    't4g.large': 0.0672,
    't4g.xlarge': 0.1344,
    't4g.2xlarge': 0.2688,
};

function elapsed(launched_at) {
    if (!launched_at) return null;
    const hrs = (Date.now() - new Date(launched_at)) / 3600000;
    return `${hrs.toFixed(2)}h`;
}

// Month-to-date cost of one running instance, as a number (null if unknown type).
export function ec2MonthToDateCost(launched_at, instance_type) {
    if (!launched_at || !instance_type) return null;
    const rate = HOURLY_PRICE[instance_type];
    if (!rate) return null;
    const now = Date.now();
    const billingStart = new Date(now);
    billingStart.setDate(1);
    billingStart.setHours(0, 0, 0, 0);
    const from = Math.max(new Date(launched_at).getTime(), billingStart.getTime());
    const hrs = (now - from) / 3600000;
    return hrs * rate;
}

function estimatedCost(launched_at, instance_type) {
    const cost = ec2MonthToDateCost(launched_at, instance_type);
    return cost == null ? null : `$${cost.toFixed(4)}`;
}

const INFO_CELL = { whiteSpace: 'nowrap', py: 0.5, px: 1 };
const HEAD_INFO_CELL = { ...INFO_CELL, fontWeight: 600 };
const GROUP_DIVIDER = { borderLeft: '1px solid', borderColor: 'divider' };

function AwsInstances({ onDeepLink }) {
    const transient = useRecoilValue(TransientState);

    const [status, setStatus] = useState({ loading: false, data: {} });
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [checkedIds, setCheckedIds] = useState(() => new Set());
    const [launchForm, setLaunchForm] = useState(null);

    const { awsLaunchTemplates } = transient.infrastructure;
    const isLocal = window.location.hostname === 'localhost';

    const refresh = useCallback(() => {
        AdminMlStatusApi(setStatus);
    }, []);

    useEffect(() => {
        refresh();
        AdminMlAwsLaunchTemplatesApi(undefined, true); // all selected regions
    }, [refresh]);

    const scaleUp = (awsLaunchTemplateId, awsLaunchTemplateName, region) => {
        setBusy(true);
        setMsg('');
        setLaunchForm(null);
        AdminMlScaleUpApi(awsLaunchTemplateId, awsLaunchTemplateName, region, (err) => {
            setBusy(false);
            setMsg(err ?? 'Scale up requested. Instance will appear shortly.');
            refresh();
        });
    };

    const openLaunch = () => setLaunchForm(true);

    const scaleDown = (templateId, instanceId, region) => {
        if (!instanceId) {
            const entry = status.data[templateId];
            const target =
                entry?.instances.find((i) => i.status === 'unhealthy') ?? entry?.instances[entry.instances.length - 1];
            if (!target) return;
            instanceId = target.id;
            region = region ?? entry?.template?.region;
        }
        setBusy(true);
        setMsg('');
        AdminMlScaleDownApi(instanceId, templateId, region, (err) => {
            setBusy(false);
            setMsg(err ?? 'Instance terminated.');
            refresh();
        });
    };

    const instances = Object.values(status.data ?? {}).flatMap((e) =>
        e.instances.map((inst) => ({ ...inst, template: e.template })),
    );

    const checkedInstances = instances.filter((i) => checkedIds.has(i.id));
    const allChecked = instances.length > 0 && checkedInstances.length === instances.length;
    const someChecked = checkedInstances.length > 0;

    const toggleChecked = (id) =>
        setCheckedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    const toggleAll = () => setCheckedIds(allChecked ? new Set() : new Set(instances.map((i) => i.id)));

    const deleteChecked = () => {
        for (const inst of checkedInstances) {
            scaleDown(inst.template_id, inst.id, inst.template?.region ?? inst.region);
        }
        setCheckedIds(new Set());
    };

    const apiItems = [
        { label: 'New', onClick: openLaunch, disabled: isLocal },
        { label: 'Refresh', onClick: refresh },
        { label: 'Delete', onClick: deleteChecked, disabled: isLocal },
    ];

    return (
        <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <AdminMenuBar apiItems={apiItems} />

            <LaunchInstanceDialog
                open={!!launchForm}
                onClose={() => setLaunchForm(null)}
                awsLaunchTemplates={awsLaunchTemplates}
                onStart={scaleUp}
            />

            <Box sx={{ overflow: 'auto', flex: 1 }}>
                {instances.length === 0 && !status.loading && !busy && (
                    <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
                        No running instances.
                    </Typography>
                )}
                {(status.loading || busy || instances.length > 0) && (
                    <Table
                        size='small'
                        sx={{ width: '100%', '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding='checkbox'>
                                    <Checkbox
                                        size='small'
                                        checked={allChecked}
                                        indeterminate={someChecked && !allChecked}
                                        onChange={toggleAll}
                                    />
                                </TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>Instance ID</TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>AWS</TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>Docker</TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>Template</TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>Type</TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>Region</TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>Public DNS / IP</TableCell>
                                <TableCell sx={{ ...HEAD_INFO_CELL, ...GROUP_DIVIDER }}>Uptime</TableCell>
                                <TableCell sx={HEAD_INFO_CELL}>Est. Cost</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.loading || busy
                                ? Array.from({ length: 3 }, (_, i) => (
                                      <TableRow key={i}>
                                          <TableCell padding='checkbox' />
                                          {Array.from({ length: 8 }, (_, j) => (
                                              <TableCell key={j} sx={INFO_CELL}>
                                                  <Skeleton />
                                              </TableCell>
                                          ))}
                                      </TableRow>
                                  ))
                                : instances.map((inst) => (
                                      <TableRow key={inst.id} selected={checkedIds.has(inst.id)} hover>
                                          <TableCell padding='checkbox'>
                                              <Checkbox
                                                  size='small'
                                                  checked={checkedIds.has(inst.id)}
                                                  onChange={() => toggleChecked(inst.id)}
                                              />
                                          </TableCell>
                                          <TableCell sx={INFO_CELL}>{inst.id}</TableCell>
                                          <TableCell sx={INFO_CELL}>
                                              {inst.checks && inst.checks.total > 0 ? (
                                                  <Chip
                                                      label={`${inst.checks.ok}/${inst.checks.total}`}
                                                      color={
                                                          inst.checks.ok === inst.checks.total ? 'success' : 'warning'
                                                      }
                                                      size='small'
                                                  />
                                              ) : (
                                                  <Chip label={inst.ec2_state ?? inst.status ?? '—'} size='small' />
                                              )}
                                          </TableCell>
                                          <TableCell sx={INFO_CELL}>
                                              {inst.docker ? (
                                                  <Chip
                                                      label={inst.docker}
                                                      color={inst.docker === 'up' ? 'success' : 'warning'}
                                                      size='small'
                                                  />
                                              ) : (
                                                  '—'
                                              )}
                                          </TableCell>
                                          <TableCell sx={INFO_CELL}>
                                              {inst.template?.id ?? inst.template_id ?? '—'}
                                          </TableCell>
                                          <TableCell sx={INFO_CELL}>{inst.instance_type ?? '—'}</TableCell>
                                          <TableCell sx={INFO_CELL}>
                                              {inst.template?.region ?? inst.region ?? '—'}
                                          </TableCell>
                                          <TableCell sx={INFO_CELL}>
                                              {inst.public_dns ?? inst.public_ip ?? '—'}
                                              {inst.public_dns && inst.public_ip && (
                                                  <Typography
                                                      component='span'
                                                      variant='caption'
                                                      color='text.secondary'
                                                      sx={{ ml: 1 }}>
                                                      ({inst.public_ip})
                                                  </Typography>
                                              )}
                                          </TableCell>
                                          <TableCell sx={{ ...INFO_CELL, ...GROUP_DIVIDER }}>
                                              {elapsed(inst.launched_at) ?? '—'}
                                          </TableCell>
                                          <TableCell sx={INFO_CELL}>
                                              {estimatedCost(inst.launched_at, inst.instance_type) ?? '—'}
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Stack>
    );
}

export { AwsInstances };
